/**
 * CR098: Section Toggles Panel
 * 
 * Expanded section toggles covering ALL card visual elements.
 * Some sections are template-backed, some are registry-backed (like ribbon).
 */

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { 
  LayoutGrid, 
  Eye,
  EyeOff,
  Tag,
  Calendar,
  DollarSign,
  Info,
  ExternalLink,
  HelpCircle,
  Ribbon,
  Bug,
  Building2,
  Store,
  TrendingUp,
  Gift,
} from "lucide-react";
import type { CardTemplateSections } from "@/providers/DealCardConfigProvider";

// ================================================================
// TYPES
// ================================================================

export interface ExtendedSectionToggles extends CardTemplateSections {
  // Additional toggles
  ribbon: boolean;
  debug_strip: boolean;
  merchant_category_chips: boolean;
  /**
   * CR225: merchant_name is kept for storage compatibility but NOT editable here.
   * The authoritative toggle is in CardStylingPanel (styling.showMerchantName).
   * This ensures single source of truth for visibility.
   */
  merchant_name: boolean;
  confidence_indicator: boolean;
  claim_button: boolean;
}

export const DEFAULT_EXTENDED_SECTIONS: ExtendedSectionToggles = {
  // Core sections (from CardTemplateSections)
  headline: true,
  promo_badges: true,
  details_pills: true,
  dates: true,
  effective_value_box: false,
  qualifying_spend_box: false,
  reward_box: false,
  eligibility_box: false,
  source_buttons: true,
  why_deal_accordion: true,
  // Extended sections
  ribbon: true,
  debug_strip: false,
  merchant_category_chips: true,
  merchant_name: true, // CR225: Synced from styling.showMerchantName - not editable here
  confidence_indicator: false,
  claim_button: true,
};

interface SectionTogglesPanelProps {
  sections: ExtendedSectionToggles;
  onChange: (section: keyof ExtendedSectionToggles, value: boolean) => void;
  isAdmin?: boolean;
}

// ================================================================
// SECTION DEFINITIONS
// ================================================================

interface SectionDef {
  key: keyof ExtendedSectionToggles;
  label: string;
  description: string;
  icon: React.ElementType;
  storageHint?: string;
  adminOnly?: boolean;
  /** CR307: Which surface(s) this block renders on */
  surface?: "front" | "back" | "both";
}

const SECTION_DEFINITIONS: SectionDef[] = [
  // Card Chrome
  { 
    key: "ribbon", 
    label: "Ribbon Banner", 
    description: "Deal type ribbon (BONUS, % BACK, etc.)",
    icon: Ribbon,
    storageHint: "Controls visibility only; content from Registry",
    surface: "front",
  },
  
  // Header Area
  { 
    key: "headline", 
    label: "Headline", 
    description: "Main deal headline/mechanic text",
    icon: Tag,
    surface: "front",
  },
  { 
    key: "promo_badges", 
    label: "Promo Badges", 
    description: "Value starburst, availability badges",
    icon: TrendingUp,
    surface: "front",
  },
  { 
    key: "merchant_category_chips", 
    label: "Merchant/Category Chips", 
    description: "Merchant logo and category pills",
    icon: Building2,
    surface: "front",
  },
  // CR225: merchant_name toggle REMOVED from here - it's controlled by Card Styling > Header > Show Merchant Name
  // This ensures single source of truth for visibility. The field still exists in sections for storage compatibility.
  
  // Details Area
  { 
    key: "details_pills", 
    label: "Detail Pills", 
    description: "Channel, region, requirements pills",
    icon: Info,
    surface: "front",
  },
  { 
    key: "dates", 
    label: "Validity Dates", 
    description: "Start/end dates display",
    icon: Calendar,
    surface: "front",
  },
  { 
    key: "confidence_indicator", 
    label: "Confidence Indicator", 
    description: "Deal confidence level badge",
    icon: TrendingUp,
    adminOnly: true,
    surface: "front",
  },
  
  // Value Boxes
  { 
    key: "effective_value_box", 
    label: "Effective Value Box", 
    description: "Calculated value display box",
    icon: DollarSign,
    surface: "front",
  },
  { 
    key: "qualifying_spend_box", 
    label: "Qualifying Spend Box", 
    description: "Required spend amount box",
    icon: DollarSign,
    surface: "front",
  },
  { 
    key: "reward_box", 
    label: "Reward Box", 
    description: "Reward/bonus amount box",
    icon: Gift,
    surface: "front",
  },
  { 
    key: "eligibility_box", 
    label: "Eligibility Box", 
    description: "Requirements and eligibility info",
    icon: HelpCircle,
    surface: "both",
  },
  
  // Actions Area — CR424: claim_button is the canonical action toggle
  { 
    key: "claim_button", 
    label: "Call-to-Action Button", 
    description: "Primary CTA / claim deal button",
    icon: ExternalLink,
    surface: "front",
  },
  
  // Debug
  { 
    key: "debug_strip", 
    label: "Debug Strip", 
    description: "Admin-only config debug info",
    icon: Bug,
    adminOnly: true,
    surface: "front",
  },
];

// ================================================================
// COMPONENT
// ================================================================

export function SectionTogglesPanel({
  sections,
  onChange,
  isAdmin = true,
}: SectionTogglesPanelProps) {
  // Filter sections based on admin status
  const visibleSections = SECTION_DEFINITIONS.filter(
    (s) => isAdmin || !s.adminOnly
  );

  // Group sections
  const cardChrome = visibleSections.filter(s => s.key === "ribbon");
  // CR225: merchant_name removed from here - now controlled only via Card Styling panel
  const headerArea = visibleSections.filter(s => 
    ["headline", "promo_badges", "merchant_category_chips"].includes(s.key)
  );
  const detailsArea = visibleSections.filter(s => 
    ["details_pills", "dates", "confidence_indicator"].includes(s.key)
  );
  const valueBoxes = visibleSections.filter(s => 
    ["effective_value_box", "qualifying_spend_box", "reward_box", "eligibility_box"].includes(s.key)
  );
  // CR424: Canonical grouping — source_buttons/why_deal_accordion removed as phantom sections
  const actionsArea = visibleSections.filter(s => 
    ["claim_button"].includes(s.key)
  );
  const expandable = visibleSections.filter(s => 
    ["debug_strip"].includes(s.key)
  );

  const renderSectionGroup = (title: string, defs: SectionDef[]) => {
    if (defs.length === 0) return null;
    
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">{title}</Label>
        <div className="space-y-1">
          {defs.map((def) => (
            <div 
              key={def.key} 
              className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <def.icon className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Label className="text-sm font-normal">{def.label}</Label>
                    {def.adminOnly && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">Admin</Badge>
                    )}
                    {def.surface && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "text-[9px] px-1.5 py-0 font-normal",
                              def.surface === "back" && "bg-muted text-muted-foreground",
                              def.surface === "both" && "bg-primary/10 text-primary",
                            )}
                          >
                            {def.surface === "front" ? "Front" : def.surface === "back" ? "Back" : "Both"}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          {def.surface === "front" && "This block renders on the Front face only"}
                          {def.surface === "back" && "This block renders on the Back face — switch to Back to preview"}
                          {def.surface === "both" && "This block renders on both Front and Back faces"}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{def.description}</p>
                  {def.storageHint && (
                    <p className="text-xs text-amber-600">{def.storageHint}</p>
                  )}
                </div>
              </div>
              <Switch
                checked={sections[def.key]}
                onCheckedChange={(v) => onChange(def.key, v)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center gap-2">
        <LayoutGrid className="h-4 w-4 text-muted-foreground" />
        <Label className="font-medium">Visible Sections</Label>
      </div>

      <p className="text-xs text-muted-foreground">
        Toggle which sections appear on deal cards for this deal type.
      </p>

      <div className="space-y-4">
        {renderSectionGroup("Card Chrome", cardChrome)}
        {renderSectionGroup("Header Area", headerArea)}
        {renderSectionGroup("Details Area", detailsArea)}
        {renderSectionGroup("Value Boxes", valueBoxes)}
        {renderSectionGroup("Actions", actionsArea)}
        {renderSectionGroup("Expandable Sections", expandable)}
      </div>

      {/* Quick Stats */}
      <div className="pt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Eye className="h-3 w-3" />
          <span>{Object.values(sections).filter(Boolean).length} visible</span>
        </div>
        <div className="flex items-center gap-1">
          <EyeOff className="h-3 w-3" />
          <span>{Object.values(sections).filter(v => !v).length} hidden</span>
        </div>
      </div>
    </div>
  );
}
